/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { TableProps } from "interfaces";
import {
  AppointmentInterface,
  CaseAppointmentInterface,
  CaseInterface,
  HeaderEnum,
  QueryResultInterface,
} from "@malmur-technologies/medicalsystem-shared";
import { Dispatch, FC, SetStateAction, SyntheticEvent, useEffect, useMemo, useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";

import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import Box from "components/generals/Box";
import {
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { RoutesConstants } from "@malmur-technologies/medicalsystem-shared";
import { useNavigate } from "react-router-dom";
import { H2 } from "components/generals/_Titles";
import { AutoCompleteOption } from "types";
import { getFromISOYYYYMMDDDateFormat } from "utils/date.utils";
import Autocomplete from "components/generals/Autocomplete";
import ButtonDialog from "components/generals/ButtonDialog";

/**
 * Extended properties for the CaseAppointmentTable component.
 * 
 * @interface CaseAppointmentTableInterface
 * @extends {TableProps<CaseAppointmentTableInterface>}
 * @property {Partial<CaseInterface>} [caseSelected] - Currently selected medical case
 * @property {Function} [setMedicalCaseList] - Callback to update case appointment list
 */
interface CaseAppointmentTableInterface extends TableProps<CaseAppointmentTableInterface> {
  caseSelected?: Partial<CaseInterface>;
  setMedicalCaseList?: Dispatch<SetStateAction<CaseAppointmentInterface[]>>;
}

/**
 * Case appointment table component for linking appointments to medical cases.
 * Manages appointments associated with specific patient cases.
 * 
 * Features:
 * - Displays appointments linked to a medical case
 * - Add appointment via autocomplete dropdown
 * - Remove appointment with delete dialog confirmation
 * - View appointment details with navigation to appointment hub
 * - Auto-loads case appointments when case is selected
 * - Loads available patient appointments for selection
 * - Formatted date display (YYYY-MM-DD)
 * - Sorted appointment options alphabetically
 * 
 * Workflow:
 * 1. Case selected triggers loading of case appointments
 * 2. Loads all patient appointments for dropdown
 * 3. User selects appointment from autocomplete
 * 4. Appointment linked to case via POST request
 * 5. Table updates with new case-appointment relation
 * 6. User can view or delete appointments from case
 * 
 * Navigation:
 * - View button navigates to appointment hub
 * - Passes appointmentId via navigation state
 * 
 * @param {CaseAppointmentTableInterface} props - Component properties
 * @param {Partial<CaseInterface>} props.caseSelected - Medical case to manage appointments for
 * @param {Function} props.setMedicalCaseList - Callback to update case appointment list
 * 
 * @returns {JSX.Element} Case appointment table with linking functionality
 */
const CaseTable: FC<CaseAppointmentTableInterface> = ({ caseSelected, setMedicalCaseList }) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [caseAppointmentList, setCaseAppointmentList] = useState<CaseAppointmentInterface[]>([]);
  const [appointmentList, setAppointmentList] = useState<AppointmentInterface[]>([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  useEffect(() => {
    if (caseSelected) {
      sendRequest(
        CoreAPI_URLs.CASE_APPOINTMENT_GET_ALL + "?medicalCase=" + caseSelected.id,
        onGetCaseHandler,
        "GET"
      );

      if (caseSelected?.patient?.id) {
        sendRequest(CoreAPI_URLs.APPOINTMENTS_GET_ALL, onGetPatientAppointments, "GET", undefined, [
          {
            header: HeaderEnum.PATIENT_ID,
            value: caseSelected.patient.id,
          },
        ]);
      }
    }
  }, [caseSelected]);

  const appointmentOptions = useMemo<AutoCompleteOption[]>(() => {
    if (appointmentList.length === 0) {
      return [];
    } else {
      return appointmentList
        .map((a) => ({
          label:
            ` ${getFromISOYYYYMMDDDateFormat(new Date(a.arrivalDateTime))} - ${a.reason}`.toUpperCase(),
          id: a.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }, [appointmentList]);

  const onGetPatientAppointments = (data: QueryResultInterface<AppointmentInterface>) => {
    setAppointmentList(data.results);
  };

  const onSelectAppointmenttHandler = async (
    _event: SyntheticEvent<Element, Event>,
    newValue: AutoCompleteOption
  ) => {
    if (newValue) {
      const selectedAppointment = appointmentList.find((p) => p.id === newValue.id);
      if (selectedAppointment) {
        const isAppointmentAlreadyAdded = caseAppointmentList.some(
          (caseAppointment) => caseAppointment.appointment.id === selectedAppointment.id
        );
        if (!isAppointmentAlreadyAdded) {
          const newCaseAppointment: CaseAppointmentInterface = {
            appointment: selectedAppointment,
            medicalCase: caseSelected,
          };
          if (caseSelected?.patient?.id) {
            const addRelation = await sendRequest(
              CoreAPI_URLs.CASE_APPOINTMENT_GENERAL_OPERATIONS,
              null,
              "POST",
              JSON.stringify(newCaseAppointment)
            );
            setCaseAppointmentList((prevList) => [...prevList, addRelation.data]);
          } else {
            setCaseAppointmentList((prevList) => [...prevList, newCaseAppointment]);
          }
          setCaseAppointmentList([...caseAppointmentList, newCaseAppointment]);
        }
      }
    }
  };

  const onGetCaseHandler = (rquestList: QueryResultInterface<CaseAppointmentInterface>) => {
    if (rquestList.results.length > 0) {
      setCaseAppointmentList(rquestList.results);
    }
  };

  const openProgressNoteHandler = async (appointment: Partial<AppointmentInterface>) => {
    const data = appointment.id;
    navigate(RoutesConstants.PROGRESS_NOTE, { state: { data } });
  };

  const deleteHandler = (appointmentToDelete: CaseAppointmentInterface) => {
    sendRequest<boolean, CaseAppointmentInterface>(
      CoreAPI_URLs.CASE_APPOINTMENT_GENERAL_OPERATIONS,
      onAppointmentDeleteHandler,
      "DELETE",
      JSON.stringify(appointmentToDelete),
      [{ header: HeaderEnum.RESOURCE_ID, value: appointmentToDelete.id }],
      { payload: appointmentToDelete }
    );
  };

  const onAppointmentDeleteHandler = (
    result: boolean,
    appointmentDeleted: CaseAppointmentInterface
  ) => {
    const indexList = caseAppointmentList.indexOf(appointmentDeleted);
    caseAppointmentList.splice(indexList, 1);
    setCaseAppointmentList([...caseAppointmentList]);
  };

  return (
    <Box sx={{ width: "100%", paddingTop: "2rem", paddingBottom: "2rem" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <H2>{t("caseAppointmentTable.appointments")}</H2>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <Autocomplete
            options={appointmentOptions}
            label={"Select to add appointment"}
            formikOutside={true}
            onChange={(_e, value) => {
              if (value) {
                onSelectAppointmenttHandler(_e, value);
              }
            }}
            noOptionsText={t("caseAppointmentTable.noOptions")}
          />
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  <TableCell align="center">{t("caseAppointmentTable.reason")}</TableCell>
                  <TableCell align="center">{t("caseAppointmentTable.date")}</TableCell>
                  <TableCell align="center">{t("caseAppointmentTable.provider")}</TableCell>
                  <TableCell align="center">{t("caseAppointmentTable.speciality")}</TableCell>
                  <TableCell align="center">{t("caseAppointmentTable.viewProgressNote")}</TableCell>
                  <TableCell align="center">{t("caseAppointmentTable.action")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {caseAppointmentList.map((medicalCase) => (
                  <TableRow key={medicalCase.id}>
                    <TableCell align="center">{medicalCase.appointment.reason}</TableCell>
                    <TableCell align="center">
                      {new Date(medicalCase.appointment.arrivalDateTime).toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      {medicalCase.appointment.provider.person.name}{" "}
                      {medicalCase.appointment.provider.person.lastname}
                    </TableCell>
                    <TableCell align="center">
                      {medicalCase.appointment.provider.specialty}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        type="button"
                        onClick={() => {
                          openProgressNoteHandler(medicalCase.appointment);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      <ButtonDialog
                        onAcceptHandle={() => {
                          const caseAppointment = caseAppointmentList.find(
                            (p) => p.id === medicalCase.id
                          );
                          if (caseAppointment) {
                            deleteHandler(caseAppointment);
                          }
                        }}
                        buttonText={t("hpitemplateTable.deleteAction")}
                        title={t("caseAppointmentTable.deleteConfirmation")}
                        description={t("caseAppointmentTable.description")}
                        labelledbyID={"HpiTemplateDelete"}
                        describedbyID={"HpiTemplateDescriptionId"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CaseTable;
